import { Flex, Tag } from 'antd'
import { HStack, Link, T } from 'govwell-ui'
import {
  ViewColumnTemplateFragment,
  ViewDataElementFragment,
  ViewDataRowFragment,
} from 'types/graphql'
import { ViewType } from 'types/graphql'

import { routes } from '@redwoodjs/router'

import { EntityDisplay } from 'src/components/TableViews/EntityDisplay'
import SettlementCell from 'src/components/TableViews/overrides/SettlementCell'
import TableCellInspectionStatus from 'src/pages/RecordPage/Record/tabs/InspectionsTab/TableCellInspectionStatus'
import { RecordTaskGroupTypeToRecordTaskGroupNameMap, formatName } from 'src/utils'
import {
  DateFormats,
  formatDate,
  formatDateInTimeZone,
  formatDuration,
  formatMoment,
} from 'src/utils/date'
import { getUrlForRecord } from 'src/utils/records'

import { FileOnRecordTypeDisplayMap, RecordTaskTypeDisplayMap } from '../../constants'
import { TransferTypeGovwellToDisplayMap } from '../../hooks/filter-inputs/use-payment-filter-inputs'
import { TaskStatusDisplay } from '../../pages/TasksPage/TaskStatusDisplay'
import { formatFee, formatPaymentMethod } from '../../utils/fee'
import { getFieldDisplayInputFromFieldFragment } from '../../utils/field'
import { FieldDisplay } from '../Field/FieldDisplay'
import RecordTaskPaymentStatusDisplay from '../RecordTaskPaymentStatusDisplay'
import { RecordTypeStatusDisplay } from '../RecordTypeStatus/RecordTypeStatusDisplay'
import { ViolationDisplay } from '../ViolationsCell/ViolationDisplay'

export type TableViewCellProps = {
  data: ViewDataElementFragment[] | null | undefined
  row: ViewDataRowFragment
  viewType: ViewType
  columnTemplate: ViewColumnTemplateFragment | null | undefined
  searchQuery?: string
  /**
   * Tailwind `max-w-[…]` class for wrap-prone variants (string, user, file,
   * entity). When set, replaces the per-variant `min-w-*` floor. Pair with
   * `minWidth="max-content"` on the parent `<Table>`. Pass a literal class
   * so Tailwind JIT detects it.
   */
  cellContentMaxWidthClassName?: string
  /** When true, record / violation / meeting links open in a new browser tab. */
  openLinksInNewTab?: boolean
}
const TableViewCell = ({
  data,
  row,
  viewType,
  columnTemplate,
  searchQuery = '',
  cellContentMaxWidthClassName,
  openLinksInNewTab,
}: TableViewCellProps) => {
  if (!data?.length) {
    return <T.Body>-</T.Body>
  }
  return (
    <Flex wrap="wrap" gap="6px">
      {data.map((datum, index) => (
        <DataElement
          cellHasMultipleData={data.length > 1}
          datum={datum}
          key={index}
          row={row}
          viewType={viewType}
          columnTemplate={columnTemplate}
          searchQuery={searchQuery}
          cellContentMaxWidthClassName={cellContentMaxWidthClassName}
          openLinksInNewTab={openLinksInNewTab}
        />
      ))}
    </Flex>
  )
}

const DataElement = ({
  cellHasMultipleData,
  datum,
  row,
  viewType,
  columnTemplate,
  searchQuery,
  cellContentMaxWidthClassName,
  openLinksInNewTab,
}: {
  cellHasMultipleData: boolean
  datum: ViewDataElementFragment
  row: ViewDataRowFragment
  viewType: ViewType | null | undefined
  columnTemplate: ViewColumnTemplateFragment | null | undefined
  searchQuery: string
  cellContentMaxWidthClassName: string | undefined
  openLinksInNewTab: boolean | undefined
}) => {
  const organization = row.organization
  const paymentProviderFormatted = organization.paymentProviderFormatted || ''
  const timeZone = organization.timeZone

  switch (datum.__typename) {
    case 'ViewDataElementBoolean':
      return <T.Body>{datum.bool === true ? 'True' : 'False'}</T.Body>
    case 'ViewDataElementDate': {
      return (
        <T.Body className="whitespace-nowrap">
          {formatDate(datum.date, DateFormats.MonthNameDateNoTime)}
        </T.Body>
      )
    }
    case 'ViewDataElementDateTime': {
      if (columnTemplate?.columnType === 'MeetingStartTime' && row.meeting) {
        return (
          <T.Body>
            <Link
              internalRoute={routes.meeting({ meetingUuid: row.meeting.uuid })}
              openInNewTab={openLinksInNewTab}
            >
              {formatDateInTimeZone(datum.dateTime, timeZone, DateFormats.MonthNameDateTime)}
            </Link>
          </T.Body>
        )
      }
      return (
        <T.Body>
          {formatDateInTimeZone(datum.dateTime, timeZone, DateFormats.MonthNameDateTime)}
        </T.Body>
      )
    }
    case 'ViewDataElementRecord': {
      const record = datum.record
      const recordIdentifier = `#${record.issuedIdentifier || record.identifier}`
      return (
        <T.Body>
          <Link
            internalRoute={getUrlForRecord({
              currentUserHasAccess: !!record.currentUserHasAccess,
              isDraft: !!record.isDraft,
              organizationSlug: record.organization?.slug,
              recordUuid: record.uuid,
              workflowId: row.recordTask?.recordTaskGroup?.workflow?.id,
            })}
            openInNewTab={openLinksInNewTab}
          >
            {record.isTest ? (
              <T.Body className="whitespace-nowrap text-orange-600">{recordIdentifier}</T.Body>
            ) : (
              <span className="whitespace-nowrap">{recordIdentifier}</span>
            )}
          </Link>
        </T.Body>
      )
    }
    case 'ViewDataElementRecordActionRequiredBy':
      return <T.Body>{datum.recordActionRequiredBy}</T.Body>
    case 'ViewDataElementRecordTaskGroupType':
      return (
        <T.Body>{RecordTaskGroupTypeToRecordTaskGroupNameMap[datum.recordTaskGroupType]}</T.Body>
      )
    case 'ViewDataElementRecordTemplate':
      return <T.Body>{datum.recordTemplate.name}</T.Body>
    case 'ViewDataElementRecordTypeStatus':
      return (
        <RecordTypeStatusDisplay
          isDraft={!datum.recordTypeStatus}
          status={datum.recordTypeStatus}
        />
      )
    case 'ViewDataElementString': {
      if (columnTemplate?.columnType === 'Settlement') {
        return <SettlementCell row={row} />
      }

      return (
        <T.Body maxLines={3} className={cellContentMaxWidthClassName ?? 'min-w-[140px]'}>
          {datum.string?.trim() ? datum.string.trim() : '-'}
        </T.Body>
      )
    }

    case 'ViewDataElementUser':
      return (
        <T.Body className={cellContentMaxWidthClassName ?? 'min-w-[140px]'}>
          {datum.user ? formatName(datum.user.firstName, datum.user.lastName) : '-'}
        </T.Body>
      )
    case 'ViewDataElementViolation': {
      return (
        <T.Body>
          <Link
            internalRoute={routes.codeComplaint({
              identifier: datum.violation.identifier,
            })}
            openInNewTab={openLinksInNewTab}
          >
            #{datum.violation.identifier}
          </Link>
        </T.Body>
      )
    }
    case 'ViewDataElementViolationStatus': {
      return <ViolationDisplay status={datum.violationStatus} />
    }
    case 'ViewDataElementViolationType': {
      return <Tag>{datum.violationType.name}</Tag>
    }
    case 'ViewDataElementInspectionTemplate': {
      return datum.inspectionTemplate.name
    }
    case 'ViewDataElementRecordTaskStatus': {
      return <TaskStatusDisplay status={datum.recordTaskStatus} />
    }
    case 'ViewDataElementRecordTaskType': {
      return RecordTaskTypeDisplayMap[datum.recordTaskType]
    }
    case 'ViewDataElementRecordTaskInspectionResult': {
      if (!datum.recordTaskInspectionResult) {
        return '-'
      }
      return (
        <Tag color={datum.recordTaskInspectionResult.isPassing ? 'green' : 'red'}>
          {datum.recordTaskInspectionResult.name}
        </Tag>
      )
    }
    case 'ViewDataElementRecordTaskInspectionStatus': {
      // this is only being used for staff inspections right now, which by definition are all isAvailable=true
      // since staff can do whatever they want to inspections, whenever
      // in the future we should migrate the isAvailable logic to the backend
      // this logic currently lives in RecordPage/Record/tabs/InspectionsTab/util.ts
      if (!row.recordTaskInspectionAttempt) {
        return '-'
      }
      return (
        <div className="min-w-[130px]">
          <TableCellInspectionStatus
            attempt={row.recordTaskInspectionAttempt}
            inspectionCoordinationType={
              row.recordTaskInspectionAttempt?.recordTaskInspection?.inspectionGroup?.record
                ?.recordTemplate?.recordSettingsDerived?.inspectionCoordinationType ??
              'SchedulingRequest'
            }
            isAvailable // TODO(IS_AVAILABLE): This is showing "Not Scheduled" to applicants even when the inspection is not available for scheduling (preferable to showing "not available yet" for ones that can be scheduled)
            timeZone={timeZone}
          />
        </div>
      )
    }
    case 'ViewDataElementOrganization': {
      return datum.organization.displayName
    }
    case 'ViewDataElementFeeType': {
      return datum.fee.name
    }
    case 'ViewDataElementFieldType': {
      return (
        <FieldDisplay
          input={getFieldDisplayInputFromFieldFragment(datum.field)}
          showDashAsFallback={!cellHasMultipleData}
        />
      )
    }
    case 'ViewDataElementGeneralLedgerNumberType': {
      return <Tag>{datum.generalLedgerNumber.glNumber}</Tag>
    }
    case 'ViewDataElementRecordTaskPaymentStatusType': {
      return <RecordTaskPaymentStatusDisplay status={datum.recordTaskPaymentStatus} />
    }
    case 'ViewDataElementRecordTypeType': {
      return datum.recordType.displayName
    }
    case 'ViewDataElementPaymentMethodType': {
      return formatPaymentMethod(datum.paymentMethod, paymentProviderFormatted)
    }
    case 'ViewDataElementMoment': {
      return datum.moment ? (
        <T.Body className="whitespace-nowrap">
          {formatMoment(datum.moment, DateFormats.FullDateWithDayOfWeek, timeZone)}
        </T.Body>
      ) : (
        '-'
      )
    }
    case 'ViewDataElementFeeSchedule': {
      return datum.feeSchedule.name
    }
    case 'ViewDataElementPaymentTransferType': {
      return datum.displayLabel ?? TransferTypeGovwellToDisplayMap[datum.paymentTransferType]
    }
    case 'ViewDataElementOrganizationFinixMerchantType': {
      return datum.organizationFinixMerchant.description
    }
    case 'ViewDataElementRecordPlansSet': {
      return (
        <T.Body className="whitespace-nowrap">{datum.recordPlansSet.versionDisplayName}</T.Body>
      )
    }
    case 'ViewDataElementRecordTypeApplicationGroup': {
      return datum.recordTypeApplicationGroup.name
    }
    case 'ViewDataElementNumber': {
      return datum.number.toString()
    }
    case 'ViewDataElementCurrencyCents': {
      return formatFee(datum.cents)
    }
    case 'ViewDataElementDuration': {
      return formatDuration(datum.seconds * 1000)
    }
    case 'ViewDataElementCompany': {
      return datum.company.name
    }
    case 'ViewDataElementFile': {
      let display: string | null = null
      if (viewType === 'FilesInModule') {
        switch (row.fileOnRecordType) {
          case 'GeneratedDocument':
            display = datum.file.name ?? null
            break
          case 'Invoice': {
            display = `Invoice for ${formatFee(row.recordTaskPayment?.totalWhenInvoiceRequestedCents)}`
            break
          }
          case 'Receipt': {
            display = `Receipt for ${formatFee(row.recordTaskPayment?.totalWhenInvoiceRequestedCents)}`
            break
          }
          default:
            display = datum.file.name ?? null
            break
        }
      } else {
        display = datum.file.name ?? null
      }
      return (
        <T.Body className={cellContentMaxWidthClassName ?? 'min-w-[150px]'}>
          <Link externalHref={datum.file.url?.signedUrl ?? ''}>{display ?? '-'}</Link>
        </T.Body>
      )
    }
    case 'ViewDataElementFileOnRecordType': {
      return FileOnRecordTypeDisplayMap[datum.fileOnRecordType]
    }
    case 'ViewDataElementEntity': {
      const isStaffParcelOrAddressColumn =
        columnTemplate?.columnType === 'EntityAddress' ||
        columnTemplate?.columnType === 'EntityParcel'
      const primaryRowEntity = row.entity
      const isPrimaryEntityCell =
        !!datum.entity?.id && !!primaryRowEntity?.id && datum.entity.id === primaryRowEntity.id
      const rowHasActiveConditions = (primaryRowEntity?.activeConditionCount ?? 0) > 0
      const showConditionSearchBadge =
        isStaffParcelOrAddressColumn &&
        isPrimaryEntityCell &&
        searchQuery.trim().length > 0 &&
        rowHasActiveConditions

      if (showConditionSearchBadge) {
        return (
          <HStack gap={2} align="center" wrap="wrap">
            <EntityDisplay
              entity={datum.entity}
              displayName={datum.displayName}
              maxWidthClassName={cellContentMaxWidthClassName}
            />
            <span className="shrink-0 rounded-md border border-blue-200 bg-blue-50 px-2 py-1">
              <T.Body className="font-normal text-blue-800">Condition Match</T.Body>
            </span>
          </HStack>
        )
      }
      return (
        <EntityDisplay
          entity={datum.entity}
          displayName={datum.displayName}
          maxWidthClassName={cellContentMaxWidthClassName}
        />
      )
    }
    case 'ViewDataElementSettlement': {
      return <SettlementCell row={row} />
    }
  }
}

export default React.memo(TableViewCell)
